import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useWorkspace2Actions } from 'components/WorkspaceEditor/Workspace2Store'
import { useProjectContentItemElementsMutation } from 'components/WorkspaceEditor/api/mutations/projectContentItemElements'
import { useProductFetcher } from 'components/WorkspaceEditor/api/useProductFetcher/useProductFetcher'
import { useProductFetcherUtils } from 'components/WorkspaceEditor/api/useProductFetcher/useProductFetcher.utils'
import { useTreeMutationsWithTrackChanges } from 'components/WorkspaceEditor/components/TrackChangesPanel/services'
import { useContentItemViewNode } from 'components/WorkspaceEditor/hooks/useContentItemViewNodeService'
import { useTransactionManager } from 'components/WorkspaceEditor/services/transactionManager'
import {
    ContentItemViewNodesType,
    GetProjectNuggetContentViewResponse,
    GetProjectProductFamilyViewResponse,
} from 'components/WorkspaceEditor/types'
import { useCallback, useMemo, useState } from 'react'
import {
    getRootArticleContentItemIds,
    processNonSiblingElementsOnContentMutation,
} from '../../../utils/contentItemViewNodesUtils'

interface UsePlainTextContentMutationProps {
    setUnsavedData?: (value: string) => void
}

export const usePlainTextContentMutation = ({
    setUnsavedData,
}: UsePlainTextContentMutationProps = {}) => {
    const { onContentViewFetchSuccess } = useProductFetcherUtils({
        overrideContentItemNodes: false,
    })

    const { executeWithOptimistic } = useTransactionManager()
    const { setTextContentElementToAdd } = useWorkspace2Actions()
    const {
        nodeToAdd,
        addNewArticle,
        addNewParagraph,
        contentItemViewNodes,
        previousContentItemViewNodes,
    } = useContentItemViewNode()
    const isNodeToAddArticle = useMemo(() => {
        return nodeToAdd?.type === 'Article'
    }, [nodeToAdd])
    const { addProjectContentItem } = useProjectContentItemElementsMutation()
    const { updateTreeViewNodes } = useTreeMutationsWithTrackChanges()
    const [
        latestContentItemViewNodes,
        setLatestContentItemViewNodes,
    ] = useState<Partial<ContentItemViewNodesType>[] | null>(null)
    const { getContentItemViewNodesAPI } = useProductFetcher({
        overrideContentItemNodes: false,
        callback: async (
            response:
                | GetProjectProductFamilyViewResponse
                | GetProjectNuggetContentViewResponse
        ) => {
            const optimisticTreeViewNodes = await onContentViewFetchSuccess(
                response,
                [],
                true,
                latestContentItemViewNodes
            )

            if (
                optimisticTreeViewNodes &&
                optimisticTreeViewNodes?.length > 0
            ) {
                updateTreeViewNodes(optimisticTreeViewNodes)
            }
        },
    })

    const handleContentMutationCompleted = useCallback(
        async (
            textElement: TextContentElement,
            isArticle = false,
            shouldSetTextContentElementToAddNull = false
        ) => {
            try {
                let rootArticleContentItemIds: string[] | null = null

                if (!isArticle) {
                    rootArticleContentItemIds = getRootArticleContentItemIds(
                        contentItemViewNodes,
                        [textElement.id]
                    )
                }
                if (shouldSetTextContentElementToAddNull) {
                    const {
                        updatedContentItem,
                    } = processNonSiblingElementsOnContentMutation(
                        contentItemViewNodes,
                        [textElement.id]
                    )
                    setLatestContentItemViewNodes(updatedContentItem)
                    setTextContentElementToAdd(null)
                } else {
                    setLatestContentItemViewNodes(null)
                }
                await getContentItemViewNodesAPI(rootArticleContentItemIds)
            } catch (error) {
                console.error(
                    'Error calling getContentItemViewNodesAPI:',
                    error
                )
            }
        },
        [
            contentItemViewNodes,
            setTextContentElementToAdd,
            getContentItemViewNodesAPI,
            updateTreeViewNodes,
            processNonSiblingElementsOnContentMutation,
            setLatestContentItemViewNodes,
            latestContentItemViewNodes,
        ]
    )

    const handleAddArticleContentExecuteOptimisticWithFallback = useCallback(
        async (fallbackState: string, textElement: TextContentElement) => {
            const variables = addNewArticle(fallbackState) || undefined

            if (!variables) {
                throw new Error(`Unsupported Type: ${nodeToAdd?.type}`)
            }
            executeWithOptimistic({
                fallbackState,
                asyncAction: () => addProjectContentItem({ variables }),
                successCbFn: async () => {
                    await handleContentMutationCompleted(
                        textElement,
                        isNodeToAddArticle,
                        true
                    )
                },
                errorCbFn: () => {
                    updateTreeViewNodes(
                        previousContentItemViewNodes as Partial<
                            ContentItemViewNodesType
                        >[]
                    )
                },
            })
        },
        [
            handleContentMutationCompleted,
            executeWithOptimistic,
            isNodeToAddArticle,
            updateTreeViewNodes,
            addProjectContentItem,
            addNewArticle,
        ]
    )

    const handleAddParagraphContentExecuteOptimisticWithFallback = useCallback(
        async (fallbackState: string, textElement: TextContentElement) => {
            const variables = addNewParagraph(fallbackState) || undefined

            if (!variables) {
                throw new Error(`Unsupported Type: ${nodeToAdd?.type}`)
            }
            executeWithOptimistic({
                fallbackState,
                asyncAction: () => addProjectContentItem({ variables }),
                successCbFn: async () => {
                    await handleContentMutationCompleted(
                        textElement,
                        isNodeToAddArticle,
                        true
                    )
                },
                errorCbFn: () => {
                    updateTreeViewNodes(
                        previousContentItemViewNodes as Partial<
                            ContentItemViewNodesType
                        >[]
                    )
                },
            })
        },
        [
            handleContentMutationCompleted,
            executeWithOptimistic,
            isNodeToAddArticle,
            updateTreeViewNodes,
            addProjectContentItem,
            addNewParagraph,
        ]
    )

    const handleAddContentExecuteOptimisticWithFallback = useCallback(
        async (fallbackState: string, textElement: TextContentElement) => {
            if (isNodeToAddArticle) {
                await handleAddArticleContentExecuteOptimisticWithFallback(
                    fallbackState,
                    textElement
                )
            } else if (!isNodeToAddArticle) {
                await handleAddParagraphContentExecuteOptimisticWithFallback(
                    fallbackState,
                    textElement
                )
            } else {
                throw new Error(`Unsupported Type: ${nodeToAdd?.type}`)
            }
        },
        [
            handleAddArticleContentExecuteOptimisticWithFallback,
            handleAddParagraphContentExecuteOptimisticWithFallback,
            isNodeToAddArticle,
            nodeToAdd?.type,
        ]
    )

    return {
        handleAddContentExecuteOptimisticWithFallback,
        handleContentMutationCompleted,
    }
}
